import React, { useState, useMemo } from 'react';
import { clampPercentage } from '../../../../utils/formProgress';
import { SearchIcon, DocumentTextIcon, CheckIcon, ChatAltIcon } from '@heroicons/react/solid';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import FormDetailsPanel from './FormDetailsPanel';
import { formatDateTime } from '../../../../utils/timestampUtils';

export default function PatientHistoryPanel({ patientData, displayMode = 'full' }) {
  const [selectedForm, setSelectedForm] = useState(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false);

  /**
   * Collects any written feedback from the patient into a flat list for display.
   * Design intent:
   * - This panel is patient-centric, so we only surface the patient's own
   *   comments here.
   * - Informant feedback is surfaced alongside informant scales in
   *   PatientScalesPanel instead, to keep mental models clear for clinicians.
   */
  const feedbackEntries = useMemo(() => {
    const entries = [];

    try {
      const pf = patientData?.formFeedback;
      const patientFeedbackText = typeof pf?.feedback === 'string' ? pf.feedback.trim() : '';

      if (patientFeedbackText) {
        // For new feedback records we persist the exact question text that was
        // shown in the completion modal (see AllFormsCompletedFeedbackModal);
        // older records may not have this field, so we treat it as optional.
        const storedQuestion =
          typeof pf?.question === 'string' && pf.question.trim()
            ? pf.question.trim()
            : null;

        entries.push({
          id: 'patient',
          role: 'Patient',
          name: `${patientData?.firstName || ''} ${patientData?.lastName || ''}`.trim() || 'Patient',
          // Only display the prompt where we have explicit metadata; for legacy
          // records without a stored question we show just the free-text
          // comment to avoid guessing from ratings.
          prompt: storedQuestion,
          feedback: patientFeedbackText,
          submittedAtLabel: formatDateTime(pf.submittedAt, 'Unknown time'),
        });
      }
    } catch (e) {
      // Defensive: never let feedback aggregation break the rest of the panel.
      // We log to console so issues can be debugged without impacting providers.
      // eslint-disable-next-line no-console
      console.warn('Failed to aggregate form feedback entries for PatientHistoryPanel:', e);
    }

    return entries;
  }, [patientData]);

  const hasAnyWrittenFeedback = feedbackEntries.length > 0;

  // Dynamically generate form titles from data model
  const formTitles = useMemo(() => {
    const formTitlesArray = [];
    
    // Get Patient History section from data model by looking up internalName
    if (!patientData?.dataModel?.['1_preAppointmentInformation']?.sections) {
      return [];
    }
    
    // Find the Patient History section
    const patientHistorySection = Object.values(patientData.dataModel['1_preAppointmentInformation'].sections)
      .find(section => section.internalName === "Patient History");
    
    if (!patientHistorySection || !patientHistorySection.domains) {
      return [];
    }
    
    // Get all form data
    const formData = patientData?.formData || {};
    
    // Map domains to form keys — prefer stable varName (Adult Autism formInstances
    // and ADHD assessment metadata both expose this).
    const sortedDomains = Object.entries(patientHistorySection.domains)
      .sort(([, a], [, b]) => (a.suggestedOrder || 0) - (b.suggestedOrder || 0));

    sortedDomains.forEach(([domainKey, domain]) => {
      const formKey = domain.varName || domainKey;
      if (!formData[formKey]) return;

      formTitlesArray.push({
        key: formKey,
        title: domain.displayName || formData[formKey]?.formTitle || formKey,
      });
    });
    
    return formTitlesArray;
  }, [patientData]);

  const handleFormClick = (formKey) => {
    setSelectedForm(formKey);
    setIsPanelOpen(true);
  };

  const handleClosePanel = () => {
    setIsPanelOpen(false);
  };

  // Function to render the progress indicator for each form
  // Notes:
  // - We clamp displayed percentages to 100 to avoid showing >100% in legacy or drifting data
  // - We also treat answeredQuestions >= totalQuestions as complete for robustness
  const renderProgressIndicator = (formKey) => {
    const formData = patientData?.formData?.[formKey];
    
    if (!formData || !formData.progress) {
      return (
        <div className="flex items-center justify-center h-6 w-6 rounded-full bg-gray-100">
          <DocumentTextIcon className="h-3.5 w-3.5 text-gray-400" />
        </div>
      );
    }
    
    const { progressPercentage, answeredQuestions, totalQuestions } = formData.progress;
    const clampedPercentage = clampPercentage(Math.round(progressPercentage || 0));
    
    // No answers
    if (progressPercentage === 0 || answeredQuestions === 0) {
      return (
        <div className="flex items-center justify-center h-6 w-6 rounded-full bg-gray-100">
          <DocumentTextIcon className="h-3.5 w-3.5 text-gray-400" />
        </div>
      );
    }
    
    // All questions answered
    if (clampedPercentage >= 100 || (answeredQuestions !== undefined && totalQuestions !== undefined && answeredQuestions >= totalQuestions)) {
      return (
        <div className="flex items-center justify-center h-6 w-6 rounded-full bg-green-100">
          <CheckIcon className="h-3.5 w-3.5 text-green-600" />
        </div>
      );
    }
    
    // Partial completion
    return (
      <div className="flex items-center justify-center h-6 w-6 rounded-full bg-violet-100">
        <DocumentTextIcon className="h-3.5 w-3.5 text-violet-600" />
      </div>
    );
  };

  return (
  <div className="w-full p-5 bg-white rounded-xl shadow border border-gray-200">
    <div className="flex justify-between items-center mb-4">
      <h2 className="text-xl font-semibold text-gray-900">Patient History</h2>
      {hasAnyWrittenFeedback && (
        /**
         * Message bubble icon: surfaces that there is written feedback from the
         * patient and/or their informants about the questionnaire journey.
         *
         * UX rationale:
         * - We keep this lightweight and non-intrusive, but place it in the
         *   Patient History header so it is discoverable next to questionnaire
         *   content.
         * - Providers can click once to see all feedback plus key metadata.
         */
        <button
          type="button"
          onClick={() => setIsFeedbackDialogOpen(true)}
          className="ml-2 mr-2 inline-flex items-center justify-center rounded-full p-1.5 text-primary hover:bg-violet-100 hover:text-violet-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500"
          aria-label="View questionnaire feedback"
        >
          <ChatAltIcon className="h-5 w-5" />
        </button>
      )}
    </div>
      <p className="text-left text-sm text-gray-600 mb-4">
        Click on any form to view the patient's responses.
      </p>
      
      <div className="border rounded-lg overflow-hidden">
      {formTitles.length === 0 ? (
        <div className="p-4 text-sm text-gray-500 text-left">
          No patient history questionnaires have been assigned yet.
        </div>
      ) : formTitles.map((form, index) => {
          const formData = patientData?.formData?.[form.key];
          // Skip forms that don't exist in the patient data
          if (!formData) return null;
          
          return (
            <div 
              key={form.key}
              className={`p-3 cursor-pointer hover:bg-gray-50 transition-colors
                ${index !== formTitles.length - 1 ? 'border-b' : ''}`}
              onClick={() => handleFormClick(form.key)}
            >
              {/* Mobile layout: stacked */}
              <div className="flex flex-col gap-2 md:hidden">
                <div className="flex items-center justify-between">
                  <div className="flex items-center flex-1 min-w-0">
                    {renderProgressIndicator(form.key)}
                    <span className="font-medium ml-3 truncate">{form.title}</span>
                  </div>
                  <div 
                    className="flex items-center justify-center w-8 h-8 text-violet-800 rounded-full hover:bg-violet-100 transition-colors flex-shrink-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleFormClick(form.key);
                    }}
                  >
                    <SearchIcon className="h-5 w-5" />
                  </div>
                </div>
                
                {/* Progress percentage on separate line for mobile */}
                {formData?.progress?.progressPercentage > 0 && (
                  <div className="ml-9">
                    <span className="text-xs font-medium text-gray-500">
                      {clampPercentage(Math.round(formData.progress.progressPercentage))}% complete
                    </span>
                  </div>
                )}
              </div>

              {/* Desktop layout: single row */}
              <div className="hidden md:flex md:justify-between md:items-center">
                <div className="flex items-center">
                  {renderProgressIndicator(form.key)}
                  <span className="font-medium ml-3">{form.title}</span>
                  
                  {/* Add progress percentage if available */}
                  {formData?.progress?.progressPercentage > 0 && (
                    <span className="ml-2 text-xs font-medium text-gray-500">
                      ({clampPercentage(Math.round(formData.progress.progressPercentage))}% complete)
                    </span>
                  )}
                </div>
                
                <div 
                  className="flex items-center gap-1 text-xs text-violet-800 font-medium rounded-full px-1 py-1 transition-all duration-200 hover:bg-violet-100 cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFormClick(form.key);
                  }}
                >
                  <SearchIcon className="h-3 w-3" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <FormDetailsPanel
        isOpen={isPanelOpen && !!selectedForm}
        formInfo={{
          key: selectedForm,
          source: 'patient'
        }}
        patientData={patientData}
        onClose={handleClosePanel}
        displayMode={displayMode}
      />

    {/* Questionnaire feedback dialog – small centered pop-up for written feedback */}
    <AlertDialog.Root open={isFeedbackDialogOpen} onOpenChange={(open) => !open && setIsFeedbackDialogOpen(false)}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/40 backdrop-blur-sm z-40" />
        <AlertDialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[95vw] max-w-xl max-h-[80vh] overflow-y-auto rounded-lg bg-white p-5 shadow-xl">
          <AlertDialog.Title className="text-lg font-semibold text-gray-900 mb-1">
            Patient Questionnaire Feedback
          </AlertDialog.Title>
          <AlertDialog.Description className="text-sm text-gray-600 mb-4">
            Written comments left by the patient after completing their questionnaires.
          </AlertDialog.Description>

          {hasAnyWrittenFeedback ? (
            <div className="space-y-4">
              {feedbackEntries.map((entry) => (
                <div
                  key={entry.id}
                  className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-left"
                >
                  <div className="flex justify-end mb-1.5">
                    <span className="text-xs text-gray-500">
                      {entry.submittedAtLabel}
                    </span>
                  </div>

                  {entry.prompt && (
                    <p className="mb-1.5 text-xs font-medium text-gray-700">
                      {entry.prompt}
                    </p>
                  )}

                  <div className="rounded-md bg-white p-2 text-sm text-gray-800 border border-gray-200">
                    {entry.feedback}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-md bg-gray-50 p-3 text-sm text-gray-700">
              No written feedback has been recorded for this patient yet.
            </div>
          )}

          <div className="mt-4 flex justify-end">
            <AlertDialog.Cancel asChild>
              <button
                type="button"
                onClick={() => setIsFeedbackDialogOpen(false)}
                className="inline-flex items-center rounded-md bg-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500"
              >
                Close
              </button>
            </AlertDialog.Cancel>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  </div>
  );
}
